import { LabelChip } from '@modules/common/components/LabelChip';
import { SelectablePredictionLabel } from '@modules/common/components/SelectablePredictionLabel';
import { Category } from '@modules/common/types';
import { getReasonOrEntityLabels } from '@modules/common/utils';
import { hasNoLabelPredictionUnits } from '@modules/feedback/components/FeedbackCard/utils/hasNoLabelPredictionUnits';
import { isFeedbackSummaryPresent } from '@modules/feedback/components/FeedbackSummary/utils';
import { useIsFeedbackOnFeedbackEnabled } from '@modules/feedback/hooks';
import { useFeedbackOnFeedbackStore } from '@modules/feedback/store';
import { FeedbackOnFeedbackActionType } from '@modules/feedback/store/feedbackOnFeedbackActionType';
import {
  EditedRecord,
  ReasonPrediction,
  TrackedKeywordPrediction,
} from '@modules/feedback/store/types';
import useFeedbackListStore from '@modules/feedback/store/useFeedbackListStore';
import { SetSelectedLabels } from '@modules/feedback/types';
import {
  getDeletedEditPredictionLabelIDs,
  getEditPrediction,
  trackFeedbackEvents,
} from '@modules/feedback/utils';
import { Box, Fade, Stack, Tooltip } from '@mui/material';
import { ManipulationType } from 'generated/graphql';
import { useEffect, useState } from 'react';
import { FeedbackEventsProps } from 'v2/analytics/events';
import {
  Feedback,
  FeedbackContentType,
  FeedbackLabel as FeedbackLabelInterface,
  FeedbackLabelType,
} from 'v2/core/entities/feedback';
import { UUID } from 'v2/core/entities/uuid';
import { getFeedbackLabelIcon } from '../utils/getFeedbackLabelIcon';

interface FeedbackLabelV2Props {
  label: FeedbackLabelInterface;
  category: Category;
  isSelected: boolean;
  feedback: Feedback;
  selectedLabels: FeedbackLabelInterface[];
  setSelectedLabels?: SetSelectedLabels;
  isDisabled?: boolean;
  onFeedbackCardLabelClick?: (selectedLabels: FeedbackLabelInterface[]) => void;
  isDeletable?: boolean;
  size: 'small' | 'large';
  isEditable?: boolean;
  isClickable?: boolean;
  shouldHideVisibilityIcon?: boolean;
}

const EVENTS_ANALYTICS_LABELS: Record<
  string,
  FeedbackEventsProps['eventName']
> = {
  ENTITY: `Feedback Details Entity Highlighted`,
  SENTIMENT: 'Feedback Details Sentiment Highlighted',
  REASON: 'Feedback Details Reason Highlighted',
  SEARCH: 'Feedback Details Search Highlighted',
};

const LABEL_FADED_IN_DELAY = 200;
const LABEL_FADED_IN_DURATION = 500;

export const FeedbackLabelV2 = ({
  label,
  category,
  isSelected,
  feedback,
  selectedLabels,
  setSelectedLabels,
  isDisabled,
  onFeedbackCardLabelClick,
  isDeletable,
  size,
  isEditable = false,
  isClickable = false,
  shouldHideVisibilityIcon = false,
}: FeedbackLabelV2Props) => {
  const [tooltipTitle, setTooltipTitle] = useState('');
  const [
    isSelectablePredictionLabelMenuOpen,
    setIsSelectablePredictionLabelMenuOpen,
  ] = useState(false);
  const dispatch = useFeedbackOnFeedbackStore(({ dispatch }) => dispatch);
  const recordInEditMode = useFeedbackOnFeedbackStore(
    ({ recordInEditMode }) => recordInEditMode,
  );
  const editedRecords = useFeedbackOnFeedbackStore(
    ({ editedRecords }) => editedRecords,
  );
  const setAudioHighlightLabelID = useFeedbackListStore(
    ({ setAudioHighlightLabelID }) => setAudioHighlightLabelID,
  );
  const { isFeedbackOnFeedbackEnabled } = useIsFeedbackOnFeedbackEnabled();

  const currentEditedRecord = editedRecords.find(
    (record) => record.id === feedback.id,
  );

  const findCurrentEditedPrediction = ({
    record,
    labelID,
  }: {
    record?: EditedRecord;
    labelID: UUID;
  }) =>
    record?.editPredictions
      ?.filter(
        (
          editPrediction,
        ): editPrediction is ReasonPrediction | TrackedKeywordPrediction =>
          editPrediction.type === FeedbackLabelType.Reason ||
          editPrediction.type === FeedbackLabelType.Entity,
      )
      .find((editPrediction) => editPrediction.prediction.id === labelID);

  const currentEditedPrediction = findCurrentEditedPrediction({
    record: currentEditedRecord,
    labelID: label.id,
  });

  const hasLabelFadedIn = isEditable
    ? currentEditedPrediction
      ? !!currentEditedPrediction.hasFadedIn
      : true
    : true;

  const [shouldShowLabel, setShouldShowLabel] = useState(hasLabelFadedIn);

  useEffect(() => {
    if (!isEditable || hasLabelFadedIn) {
      return;
    }

    const timerID = setTimeout(() => {
      setShouldShowLabel(true);
      dispatch({
        type: FeedbackOnFeedbackActionType.SET_HAS_PREDICTION_FADED_IN,
        payload: {
          feedbackID: feedback.id,
          labelID: label.id,
          hasFadedIn: true,
        },
      });
    }, LABEL_FADED_IN_DELAY);

    return () => clearTimeout(timerID);
  }, [
    currentEditedRecord,
    dispatch,
    feedback.id,
    hasLabelFadedIn,
    isEditable,
    label.id,
  ]);

  const onDeleteClick = () => {
    const isLabelDeleteable =
      label.type === FeedbackLabelType.Reason ||
      label.type === FeedbackLabelType.Entity;

    const newlyAddedReasonOrTrackedKeywordIndex =
      recordInEditMode?.editPredictions
        ?.filter(
          (
            editPrediction,
          ): editPrediction is ReasonPrediction | TrackedKeywordPrediction =>
            editPrediction.manipulationType === ManipulationType.Add &&
            (editPrediction.type === FeedbackLabelType.Reason ||
              editPrediction.type === FeedbackLabelType.Entity),
        )
        .findIndex(
          (editPrediction) => editPrediction.prediction.id === label.id,
        );

    if (
      newlyAddedReasonOrTrackedKeywordIndex !== undefined &&
      newlyAddedReasonOrTrackedKeywordIndex > -1
    ) {
      dispatch({
        type: FeedbackOnFeedbackActionType.SET_RECORD_IN_EDIT_MODE,
        payload: {
          id: feedback.id,
          editPredictions:
            recordInEditMode?.editPredictions?.filter(
              (editPrediction) =>
                editPrediction.type === 'SENTIMENT' ||
                editPrediction.prediction.id !== label.id,
            ) ?? [],
        },
      });
      return;
    }

    if (!isLabelDeleteable) {
      return;
    }

    const editPrediction = getEditPrediction({ label, category });

    dispatch({
      type: FeedbackOnFeedbackActionType.SET_RECORD_IN_EDIT_MODE,
      payload: {
        id: feedback.id,
        editPredictions: [
          ...(recordInEditMode?.editPredictions ?? []),
          editPrediction,
        ],
      },
    });
  };

  const handleClick = (label: FeedbackLabelInterface, isSelected: boolean) => {
    if (isDeletable || !setSelectedLabels || isClickActionDisabled) {
      return;
    }

    if (isSelected) {
      const updatedSelectedLabels =
        selectedLabels?.filter((l) => l.id !== label.id) ?? [];
      setSelectedLabels(updatedSelectedLabels);
      if (feedback.content.type === FeedbackContentType.Audio) {
        setAudioHighlightLabelID(undefined, feedback.id);
      }
      return;
    }

    const updatedSelectedLabels = [label, ...(selectedLabels ?? [])];
    onFeedbackCardLabelClick?.(updatedSelectedLabels);
    trackFeedbackEvents({
      eventName: 'Feedback Card Expanded',
      feedbackID: feedback.id,
    });
    setSelectedLabels(updatedSelectedLabels);
    if (feedback.content.type === FeedbackContentType.Audio) {
      setAudioHighlightLabelID(label.id, feedback.id);
    }
    if (label.type !== FeedbackLabelType.Cluster) {
      try {
        trackFeedbackEvents({
          eventName: EVENTS_ANALYTICS_LABELS[label.type],
          label,
          feedbackID: feedback.id,
        });
      } catch {}
    }
  };

  const hasFeedbackSummary = isFeedbackSummaryPresent(feedback);

  const hasNoPredictionToHighlight =
    !hasFeedbackSummary && hasNoLabelPredictionUnits(label);

  const isClickActionDisabled = isDisabled || hasNoPredictionToHighlight;

  useEffect(() => {
    if (!isClickable || isSelectablePredictionLabelMenuOpen) {
      setTooltipTitle('');
      return;
    }

    if (hasNoPredictionToHighlight) {
      setTooltipTitle('Highlight not available');
      return;
    }

    if (isClickActionDisabled || !setSelectedLabels) {
      setTooltipTitle('');
      return;
    }

    if (isSelected) {
      setTooltipTitle('Remove from the highlights');
      return;
    }

    setTooltipTitle('Show in the highlights');
  }, [
    hasNoPredictionToHighlight,
    isClickActionDisabled,
    isClickable,
    isSelectablePredictionLabelMenuOpen,
    isSelected,
    setSelectedLabels,
  ]);

  const deletedEditPredictionLabelIDs =
    getDeletedEditPredictionLabelIDs(recordInEditMode);

  const isLabelDeleted = deletedEditPredictionLabelIDs.includes(label.id);

  const getOnDelete = () => {
    if (!(isDeletable && !isLabelDeleted)) {
      return undefined;
    }

    return onDeleteClick;
  };

  const reasonOrEntityLabel = getReasonOrEntityLabels([label])[0];

  return (
    <Box key={label.id}>
      <Tooltip
        title={tooltipTitle}
        placement="top"
        arrow
        disableInteractive
        enterDelay={1000}
        enterNextDelay={1000}
        componentsProps={{
          popper: {
            sx: {
              zIndex: 1000, // Because it shows up over edit prediction drawer
            },
          },
        }}
      >
        {isFeedbackOnFeedbackEnabled ? (
          <Fade
            in={shouldShowLabel}
            appear={false}
            timeout={LABEL_FADED_IN_DURATION}
          >
            <Stack>
              <SelectablePredictionLabel
                label={reasonOrEntityLabel}
                category={category}
                feedback={feedback}
                isSelected={isSelected}
                onClick={() => handleClick(reasonOrEntityLabel, isSelected)}
                disabled={isLabelDeleted}
                size={size}
                isEditable={isEditable}
                isClickable={isClickable}
                isMenuOpen={isSelectablePredictionLabelMenuOpen}
                setIsMenuOpen={setIsSelectablePredictionLabelMenuOpen}
                shouldHideVisibilityIcon={shouldHideVisibilityIcon}
              />
            </Stack>
          </Fade>
        ) : (
          <LabelChip
            displayName={label.displayName}
            category={category}
            isSelected={isSelected}
            renderLabelIcon={() => getFeedbackLabelIcon(label)}
            clickable={!isClickActionDisabled && !isDeletable}
            disabled={isLabelDeleted}
            onClick={() => handleClick(label, isSelected)}
            onDelete={getOnDelete()}
          />
        )}
      </Tooltip>
    </Box>
  );
};

export default FeedbackLabelV2;
